import { createAsyncThunk } from "@reduxjs/toolkit";

import * as checkoutAPI from "../../../api/checkout";
import { IAddress } from "../../../models/address";
import { reloadBasket } from "../../../store/basket/thunks";
import { HttpError } from "../../../utils/ajax";
import { IReduxState } from "../reducers.interfaces";
import { getShippingAddress } from "../utils";

/**
 * Arguments for saveShippingAddress thunk.
 */
interface SaveShippingAddressArgs {
    bypassAddressValidation?: boolean;
}

/**
 * Save the shipping address for checkout.
 *
 * This thunk saves the shipping address to the checkout session and
 * reloads the basket to recalculate taxes and shipping-dependent offers.
 * Supports bypassing address validation for cases where the user
 * confirms their non-standard address.
 *
 * @param {boolean} [args.bypassAddressValidation=false] - Skip address validation for non-standard addresses
 * @returns {Promise<IAddress>} Promise resolving to the saved address (potentially normalized by SureAddress)
 *
 * @example
 * // Save shipping address with validation
 * dispatch(saveShippingAddress({}));
 *
 * @example
 * // Save non-standard address bypassing validation
 * dispatch(saveShippingAddress({ bypassAddressValidation: true }));
 *
 * @throws {Error} If address validation fails and bypass not set
 *
 * @sideEffects
 * - Reads shipping address from `state.form.shipping_*` fields
 * - Makes API call to save address via checkoutAPI.saveShippingAddress
 * - Dispatches reloadBasket thunk to reload basket with updated taxes
 * - Updates `data.basket` with reloaded basket via extraReducers
 * - May normalize address fields via SureAddress validation
 */
export const saveShippingAddress = createAsyncThunk<
    IAddress,
    SaveShippingAddressArgs,
    { rejectValue: { response: { body: unknown; status: number } } }
>(
    "checkout/saveShippingAddress",
    async (args, { getState, dispatch, rejectWithValue }) => {
        const state = getState() as { checkout: IReduxState };
        const shippingAddr = getShippingAddress(state.checkout);
        let addr: IAddress;
        try {
            addr = await checkoutAPI.saveShippingAddress(
                shippingAddr,
                args.bypassAddressValidation,
            );
        } catch (err) {
            if (err instanceof HttpError) {
                return rejectWithValue({
                    response: { body: err.body, status: err.status },
                });
            }
            throw err;
        }
        // Reload basket to get updated taxes and basket offers after shipping address change
        await dispatch(reloadBasket()).unwrap();
        return addr;
    },
);
